<template>
  <div v-if="loading" class="rewards-banner rewards-banner--loading">
    <Loader :show="true" />
  </div>
  <div
    v-else-if="rewardSlides.length"
    class="rewards-banner"
    :class="{ [`rewards-banner--${currentSlide.theme}`]: currentSlide.theme }"
  >
    <div class="rewards-banner__slider">
      <div
        class="rewards-banner__visuals"
        :class="{
          'rewards-banner__visuals--single': currentSlideImageSrc,
        }"
      >
        <img
          v-if="currentSlideImageSrc"
          class="rewards-banner__hero-img"
          :src="currentSlideImageSrc"
          alt=""
          @error="currentSlideImageFailed = true"
        />
        <template v-else>
          <div class="rewards-banner__photo rewards-banner__photo--bike"></div>
          <div class="rewards-banner__photo rewards-banner__photo--person"></div>
          <div class="rewards-banner__photo rewards-banner__photo--car"></div>
          <div class="rewards-banner__photo rewards-banner__photo--lifestyle"></div>
        </template>
      </div>

      <div class="rewards-banner__content">
        <h2 class="rewards-banner__title">{{ currentSlide.title }}</h2>
        <p
          v-if="currentSlide.description"
          class="rewards-banner__subtitle"
          v-html="currentSlide.description"
        ></p>
        <div class="rewards-banner__footer">
          <span v-if="currentSlide.pointsLabel" class="rewards-banner__points">
            {{ currentSlide.pointsLabel }}
          </span>
          <div
            v-if="rewardSlides.length > 1"
            class="rewards-banner__dots"
            :aria-label="$t('portalHome.rewards.slidesAriaLabel')"
          >
            <button
              v-for="(slide, index) in rewardSlides"
              :key="slide.id"
              class="rewards-banner__dot"
              :class="{ 'rewards-banner__dot--active': index === activeSlideIndex }"
              type="button"
              :aria-label="$t('portalHome.rewards.showSlideAriaLabel', { number: index + 1 })"
              :aria-current="index === activeSlideIndex"
              @click="activeSlideIndex = index"
            ></button>
          </div>
          <button
            class="rewards-banner__cta"
            type="button"
            :disabled="!currentSlide.linkUrl"
            @click="openSlideLink(currentSlide)"
          >
            {{ $t("portalHome.rewards.claimButton") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Loader from "@/components/General/Loader/index.vue";
import { resolveBundledAssetUrl } from "@/utils/resolveBundledAssetUrl";
import { usePortalRewardsSlides } from "@/composables/usePortalRewardsSlides";

const { rewardSlides, loading } = usePortalRewardsSlides();

const activeSlideIndex = ref(0);
const currentSlideImageFailed = ref(false);
const autoplayInterval = ref(null);
const AUTOPLAY_DELAY_MS = 2000;

const currentSlide = computed(
  () => rewardSlides.value[activeSlideIndex.value] ?? rewardSlides.value[0],
);

const currentSlideImageSrc = computed(() => {
  const src = currentSlide.value?.imageSrc;
  if (!src || currentSlideImageFailed.value) {
    return "";
  }
  return resolveBundledAssetUrl(src);
});

function openSlideLink(slide) {
  if (!slide?.linkUrl) {
    return;
  }

  window.open(slide.linkUrl, slide.newTab ? "_blank" : "_self");
}

function showNextSlide() {
  if (rewardSlides.value.length < 2) {
    return;
  }

  activeSlideIndex.value = (activeSlideIndex.value + 1) % rewardSlides.value.length;
}

function stopAutoplay() {
  if (!autoplayInterval.value) return;
  clearInterval(autoplayInterval.value);
  autoplayInterval.value = null;
}

function startAutoplay() {
  stopAutoplay();

  if (rewardSlides.value.length < 2) {
    return;
  }

  autoplayInterval.value = setInterval(showNextSlide, AUTOPLAY_DELAY_MS);
}

watch(activeSlideIndex, () => {
  currentSlideImageFailed.value = false;
});

watch(
  () => rewardSlides.value.length,
  (length) => {
    if (activeSlideIndex.value >= length) {
      activeSlideIndex.value = 0;
    }
    startAutoplay();
  },
);

onMounted(startAutoplay);
onBeforeUnmount(stopAutoplay);
</script>

<style scoped lang="scss">
@import "@/styles/portal-home/rewards-catalog-banner";

.rewards-banner--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
}
</style>
